// Contents of this file were produced with the help of a code generation tool and
// subsequently reviewed and edited by a human.
//
// While some of the code was created by an AI-assisted service, manual adjustments
// have been made to ensure correctness, readability, functionality and compliance
// to Avalara's coding standards and guidelines.
// Any future modifications should be approached with caution to avoid
// overwriting the manual changes.
//
//
// This source code is owned by Avalara Inc. Unauthorized copying of this file,
// via any medium, is strictly prohibited. Proprietary and confidential.
//
// For more information, refer to the documentatin or contact the development team.

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import _, { kebabCase } from 'lodash';
import { FormikValues, useFormik } from 'formik';
import FieldInput from '../../../components/FieldInput/FieldInput';
import FieldDate from '../../../components/FieldDate/FieldDate';
import ToastMessage from '../../../components/ToastMessage/ToastMessage';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import validateEmpty from '../../../utils/validateEmpty';
import { getAssessmentNotice, getErrorMessage, updateAssessmentNotice } from '../../../services';
import {
    IErrorBase,
    IToastMessage,
    ReturnsItem,
} from '../../../interface';
import style from './AssessmentNoticeSetup.module.scss';
import { AxiosResponse } from 'axios';
import {
    getFirstDateOfThisYear,
    getLastDateOfThisYear,
} from '../../../utils/date';
import Loader, { LoaderIndicator } from '../../../components/Loader/Loader';
import DiscardChangeConfirmModal from '../../../components/DiscardChangeConfirmModal/DiscardChangeConfirmModal';
import TooltipWrapper from '../../../components/TooltipWrapper/TooltipWrapper';
import { AssessmentNoticeStatus, TOOLTIP_SECTIONS } from '../../../enums';
import { useAppDispatch } from '../../../redux/store';
import { currencyToNumber, formatCurrency, ratioBackendToFrontend } from '../../../utils/formatNumber';
import FieldTextarea from '../../../components/FieldTextarea/FieldTextarea';
import Divider from '../../../components/Divider/Divider';

// Init formik values
const newInit = {
    returnId: null,
    return: {} as ReturnsItem,
    noticeStatus: '',
    noticeMarketValue: '',
    noticeAssessedValue: '',
    appealDeadline: '',
    finalMarketValue: '',
    finalAssessedValue: '',
    appealNotes: '',
};

// Define the required fields
const requiredField = [
    'appealDeadline',
];

function AssessmentNoticeSetup() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const [navBlock, setNavBlock] = useState<boolean>();

    const breadcrumb = [
        {
            value: 'Taxes',
            href: '/taxes',
        },
        { value: id ? 'Edit Notice Values' : 'Enter Notice Values' },
    ];
    const [fetchingAssessmentNotice, setFetchingAssessmentNotice] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleResponse = (promise: Promise<AxiosResponse>) => {
        return promise
            .then((e: AxiosResponse<IErrorBase>) => {
                if (_.inRange(e.status, 200, 299)) {
                    navigate(`/taxes`);
                }
            })
            .catch((e) => {
                setToastMessage({
                    visible: true,
                    message: getErrorMessage(e),
                    type: "error",
                });
            })
            .finally(() => setSubmitting(false));
    }

    const calculateAssessedValue = (marketValue: number, assessmentRatio: number) => {
        const assessedValue = currencyToNumber(marketValue ?? 0) * (assessmentRatio ?? 1);
        return Math.round(assessedValue);
    }

    // If Accepting or Settling, and final values are not set, set them to the notice values
    const setFinalValues = () => {
        if (!formik.values.finalMarketValue) {
            formik.setFieldValue('finalMarketValue', formik.values.noticeMarketValue);
        }
        if (!formik.values.finalAssessedValue) {
            formik.setFieldValue('finalAssessedValue', formik.values.noticeAssessedValue);
        }
    }

    // Flag for submit or not
    const [issubmit, setissubmit] = useState<boolean>(false);
    const [submitToast, setSubmitToast] = useState<boolean>(false);
    const [initialValues, setinitialValues] = useState(newInit);
    const formik = useFormik<FormikValues>({
        initialValues: initialValues,
        enableReinitialize: true,
        validate: (values) => {
            // Validate form,if Same As Mailing Address checkbox is checked,not validate the package fields
            if (issubmit) {
                const _required = _.cloneDeep(requiredField);
                const errors = _.defaults(
                    validateEmpty(_.pick(values, _required)),
                );
                const result = _.every(_.values(errors), (e: string | []) => {
                    return _.every(e, _.isEmpty);
                });

                if (!result) {
                    if (submitToast) {
                        setSubmitToast(false);
                        setToastMessage({
                            visible: true,
                            message:
                                'The Assessment Notice has some errors that need to be fixed. Please address these to move forward.',
                            type: 'error',
                        });
                    }
                    return errors;
                }
            }
        },
        onSubmit: (formValues) => {
            setNavBlock(false);

            if (formValues.id) {
                if (formValues.noticeMarketValue) {
                    formValues.noticeMarketValue = currencyToNumber(formValues.noticeMarketValue);
                }
                if (formValues.noticeAssessedValue) {
                    formValues.noticeAssessedValue = currencyToNumber(formValues.noticeAssessedValue);
                }
                if (formValues.finalMarketValue) {
                    formValues.finalMarketValue = currencyToNumber(formValues.finalMarketValue);
                }
                if (formValues.finalAssessedValue) {
                    formValues.finalAssessedValue = currencyToNumber(formValues.finalAssessedValue);
                }
                setSubmitting(true);
                handleResponse(updateAssessmentNotice(formValues.id, formValues));
            }
        }
    });

    useEffect(() => {
        if (id) {
            window.scrollTo(0, 0);
            setFetchingAssessmentNotice(true);
            getAssessmentNotice(id)
                .then((d) => {
                    setinitialValues(d.data);
                    setFetchingAssessmentNotice(false);
                })
                .catch(() => {
                    setFetchingAssessmentNotice(false);
                });
        }
    }, [id]);

    const renderPropertyInfoField = (
        label: string,
        value: string | undefined,
    ) => {
        return (
            <div className={style['info-field']}>
                <TooltipWrapper
                    tooltipSection={TOOLTIP_SECTIONS.AccountInfo}
                    tooltipKey={label}
                >
                    <div>
                        <div className={style['info-field-label']}>{label}</div>
                        <div className={style['info-field-value']}>{value}</div>
                    </div>
                </TooltipWrapper>
            </div>
        );
    };

    // reset valid by click the close icon of error message box
    const [toastMessage, setToastMessage] = useState<IToastMessage>();

    return (
        <>
            <div className={style['main-content']}>
                {/* Braedcrumb */}
                <Breadcrumb items={breadcrumb} />
                {/* Error message box,if there are errors message */}
                {toastMessage?.visible && (
                    <div className={style['toast']}>
                        <ToastMessage
                            status={toastMessage}
                            className='validate-error'
                            visiableHandler={(value) =>
                                setToastMessage({ ...toastMessage, visible: value })
                            }
                        />
                    </div>
                )}
                <h2>
                    {id ? (
                        <TooltipWrapper
                            tooltipSection={TOOLTIP_SECTIONS.PageTitle}
                            tooltipKey='Edit Assessment Notice'
                        >
                            <span>Edit Assessment Notice</span>
                        </TooltipWrapper>
                    ) : (
                        <TooltipWrapper
                            tooltipSection={TOOLTIP_SECTIONS.PageTitle}
                            tooltipKey='Add Assessment Notice'
                        >
                            <span>Add Assessment Notice</span>
                        </TooltipWrapper>
                    )}
                </h2>
                <form className={style['main']} onSubmit={formik.handleSubmit}>
                    <div className={style['info-row']}>
                        {renderPropertyInfoField(
                            'Assessor Name',
                            initialValues?.return?.assessorAccountData?.assessor?.name,
                        )}
                        {renderPropertyInfoField(
                            'Assessor Account Number',
                            initialValues?.return?.assessorAccountData?.number,
                        )}
                        {renderPropertyInfoField(
                            'Property Number',
                            initialValues?.return?.assessorAccountData?.property?.number,
                        )}
                        {renderPropertyInfoField(
                            'Property Name',
                            initialValues?.return?.assessorAccountData?.property?.name,
                        )}
                        {renderPropertyInfoField(
                            'Property Address',
                            (initialValues?.return?.assessorAccountData?.property?.address.line1
                                ? initialValues?.return?.assessorAccountData?.property?.address.line1 + ', '
                                : '') +
                            (initialValues?.return?.assessorAccountData?.property?.address.line2
                                ? initialValues?.return?.assessorAccountData?.property?.address.line2 + ', '
                                : '') +
                            (initialValues?.return?.assessorAccountData?.property?.address.state?.abbreviation
                                ? initialValues?.return?.assessorAccountData?.property?.address.state
                                    ?.abbreviation + ', '
                                : '') +
                            (initialValues?.return?.assessorAccountData?.property?.address.postalCode || ''),
                        )}
                    </div>
                    {/* Company form list */}
                    <div className={classNames(style['section'])}>
                        {/* Form header,remove icon only appear when form count > 1 */}
                        <div className={style['form']}>
                            {/* Return Info */}
                            <fieldset className={style['form-section']}>
                                <legend>
                                    <TooltipWrapper
                                        tooltipSection={TOOLTIP_SECTIONS.Common}
                                        tooltipKey='Notice Info'
                                    >
                                        <span>Return Information</span>
                                    </TooltipWrapper>
                                </legend>
                                <div className={classNames(style['row'], 'table-layout')}>
                                    <div className={style['column']}>
                                        Return Status:
                                    </div>
                                    <div className={classNames(style['column'], style['text-right'])}>
                                        <span className={classNames('badge-status', kebabCase(initialValues?.return?.status))}>
                                            {initialValues?.return?.status}
                                        </span>
                                    </div>
                                </div>
                                <div className={classNames(style['row'], 'table-layout')}>
                                    <div className={style['column']}>
                                        Reported Cost:
                                    </div>
                                    <div className={classNames(style['column'], style['text-right'])}>
                                        {formatCurrency(initialValues?.return?.totalReportedCost)}
                                    </div>
                                </div>
                            </fieldset>
                            {/* Return Info */}
                            <fieldset className={style['form-section']}>
                                <div className={classNames(style['row'], 'table-layout')}>
                                    <div className={style['column']}>
                                        Assessment Ratio:
                                    </div>
                                    <div className={classNames(style['column'], style['text-right'])}>
                                        {ratioBackendToFrontend(initialValues?.return?.assessorAccountData?.assessor?.taxYears[0]?.assessmentRatio)}%
                                    </div>
                                </div>
                                <div className={classNames(style['row'], 'table-layout')}>
                                    <div className={style['column']}>
                                        Reported Assessed Value:
                                    </div>
                                    <div className={classNames(style['column'], style['text-right'])}>
                                        {formatCurrency(initialValues?.return?.totalAssessedValue)}
                                    </div>
                                </div>
                            </fieldset>
                            {/* Initial Notice Info */}
                            <fieldset className={style['form-section']}>
                                <legend>
                                    <TooltipWrapper
                                        tooltipSection={TOOLTIP_SECTIONS.Common}
                                        tooltipKey='Notice Info'
                                    >
                                        <span>Notice Information</span>
                                    </TooltipWrapper>
                                </legend>
                                <div className={classNames(style['row'], 'table-layout')}>
                                    <div className={style['column']}>
                                        Notice Status:
                                    </div>
                                    <div className={classNames(style['column'], style['text-right'])}>
                                        <span className={classNames('badge-status', kebabCase(initialValues?.noticeStatus))}>
                                            {initialValues?.noticeStatus}
                                        </span>
                                    </div>
                                </div>
                                <div className={style['row']}>
                                    <div className={style['column']}>
                                        Appeal Deadline<span className={style['requiredMarker']}>*</span>:
                                    </div>
                                    <div className={style['column']}>
                                        <FieldDate
                                            primaryFormat='MM/DD/YYYY'
                                            labelText=''
                                            minDate={getFirstDateOfThisYear()}
                                            maxDate={getLastDateOfThisYear()}
                                            selectValue={formik.values.appealDeadline}
                                            onSelect={(value) =>
                                                formik.setFieldValue(`appealDeadline`, value)
                                            }
                                            error={
                                                formik.errors &&
                                                (formik.errors as FormikValues).appealDeadline
                                            }
                                            required
                                        />
                                    </div>
                                </div>
                                <div className={classNames(style['row'], 'table-layout')}>
                                    <div className={style['column']}>
                                        Notice Market Value:
                                    </div>
                                    <div className={style['column']}>
                                        <FieldInput
                                            classnames={style['text-right']}
                                            labelText=''
                                            placeholder='Enter'
                                            value={formatCurrency(formik.values?.noticeMarketValue, false)}
                                            onChange={(newVal) => {
                                                formik.setFieldValue(
                                                    `noticeMarketValue`,
                                                    newVal ? formatCurrency(newVal, false) : null,
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className={classNames(style['row'], 'table-layout')}>
                                    <div className={style['column']}>
                                        Assessment Ratio:
                                    </div>
                                    <div className={classNames(style['column'], style['text-right'])}>
                                        {ratioBackendToFrontend(initialValues?.return?.assessorAccountData?.assessor?.taxYears[0]?.assessmentRatio)}%
                                    </div>
                                </div>
                                <div className={classNames(style['row'])}>
                                    <div className={style['column']}>
                                    </div>
                                    <div className={classNames(style['column'], style['text-right'], style['no-padding-bottom'])}>
                                        <i
                                            role='button'
                                            className={classNames('link', style['toggle-link'])}
                                            onClick={() =>
                                                formik.setFieldValue(
                                                    'noticeAssessedValue',
                                                    calculateAssessedValue(
                                                        currencyToNumber(formik.values.noticeMarketValue ?? 0),
                                                        _.toNumber(initialValues?.return?.assessorAccountData?.assessor?.taxYears[0]?.assessmentRatio ?? 1)
                                                    )
                                                )
                                            }
                                        >
                                            Calculate
                                        </i>
                                    </div>
                                </div>
                                <div className={classNames(style['row'], 'table-layout')}>
                                    <div className={style['column']}>
                                        Notice Assessed Value:
                                    </div>
                                    <div className={style['column']}>
                                        <FieldInput
                                            classnames={style['text-right']}
                                            labelText=''
                                            placeholder='Enter'
                                            value={formatCurrency(formik.values?.noticeAssessedValue, false)}
                                            onChange={(newVal) => {
                                                formik.setFieldValue(
                                                    `noticeAssessedValue`,
                                                    newVal ? formatCurrency(newVal, false) : null,
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                                <Divider />
                            </fieldset>
                            {/* Final Notice Info */}
                            {
                                initialValues.noticeStatus !== AssessmentNoticeStatus.Missing && (
                                    <fieldset className={style['form-section']}>
                                        <div className={classNames(style['row'], 'table-layout')}>
                                            <div className={style['column']}>
                                                Final Market Value:
                                            </div>
                                            <div className={style['column']}>
                                                <FieldInput
                                                    classnames={style['text-right']}
                                                    labelText=''
                                                    placeholder='Enter'
                                                    value={formatCurrency(formik.values?.finalMarketValue, false)}
                                                    onChange={(newVal) => {
                                                        formik.setFieldValue(
                                                            `finalMarketValue`,
                                                            newVal ? formatCurrency(newVal, false) : null,
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className={classNames(style['row'], 'table-layout')}>
                                            <div className={style['column']}>
                                                Assessment Ratio:
                                            </div>
                                            <div className={classNames(style['column'], style['text-right'])}>
                                                {ratioBackendToFrontend(initialValues?.return?.assessorAccountData?.assessor?.taxYears[0]?.assessmentRatio)}%
                                            </div>
                                        </div>
                                        <div className={classNames(style['row'])}>
                                            <div className={style['column']}>
                                            </div>
                                            <div className={classNames(style['column'], style['text-right'], style['no-padding-bottom'])}>
                                                <i
                                                    role='button'
                                                    className={classNames('link', style['toggle-link'])}
                                                    onClick={() =>
                                                        formik.setFieldValue(
                                                            'finalAssessedValue',
                                                            calculateAssessedValue(
                                                                currencyToNumber(formik.values.finalMarketValue ?? 0),
                                                                _.toNumber(initialValues?.return?.assessorAccountData?.assessor?.taxYears[0]?.assessmentRatio ?? 1)
                                                            )
                                                        )
                                                    }
                                                >
                                                    Calculate
                                                </i>
                                            </div>
                                        </div>
                                        <div className={classNames(style['row'], 'table-layout')}>
                                            <div className={style['column']}>
                                                Final Assessed Value:
                                            </div>
                                            <div className={style['column']}>
                                                <FieldInput
                                                    classnames={style['text-right']}
                                                    labelText=''
                                                    placeholder='Enter'
                                                    value={formatCurrency(formik.values?.finalAssessedValue, false)}
                                                    onChange={(newVal) => {
                                                        formik.setFieldValue(
                                                            `finalAssessedValue`,
                                                            newVal ? formatCurrency(newVal, false) : null,
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </fieldset>
                                )
                            }

                            {/* Appeal Notes */}
                            <fieldset className={style['form-section']}>
                                <div className={classNames(style['row'], 'table-layout')}>
                                    <div className={style['column']}>
                                        Assessment Notes:
                                    </div>
                                    <div className={style['column']}>
                                        <FieldTextarea
                                            classnames={style['text-right']}
                                            value={formik.values?.appealNotes ?? ''}
                                            onChange={(newVal) => formik.setFieldValue(`appealNotes`, newVal)}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                    </div>
                    <div className={style['footer']}>
                        {/* Submit link,it change the flag of submit */}
                        <div className={style['buttons']}>
                            <TooltipWrapper
                                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                                tooltipKey='Save and complete'
                            >
                                <button
                                    type='submit'
                                    className='primary'
                                    onClick={() => {
                                        formik.setFieldValue('noticeStatus', AssessmentNoticeStatus.Appealed);
                                        setissubmit(true);
                                        setSubmitToast(true);
                                    }}
                                    disabled={submitting}
                                >
                                    Save and Appeal
                                    {submitting ? (
                                        <LoaderIndicator
                                            className='button-loading'
                                            loading={true}
                                        />
                                    ) : null}
                                </button>
                            </TooltipWrapper>
                            <TooltipWrapper
                                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                                tooltipKey='Save and complete'
                            >
                                <button
                                    type='submit'
                                    className='primary'
                                    onClick={() => {
                                        setFinalValues();
                                        const newStatus = formik.values.noticeStatus === AssessmentNoticeStatus.Missing ?
                                            AssessmentNoticeStatus.Accepted :
                                            AssessmentNoticeStatus.Settled;
                                        formik.setFieldValue('noticeStatus', newStatus);
                                        setissubmit(true);
                                        setSubmitToast(true);
                                    }}
                                    disabled={submitting}
                                >
                                    Save and {initialValues.noticeStatus === AssessmentNoticeStatus.Missing ? 'Accept' : 'Settle'}
                                    {submitting ? (
                                        <LoaderIndicator
                                            className='button-loading'
                                            loading={true}
                                        />
                                    ) : null}
                                </button>
                            </TooltipWrapper>
                            <TooltipWrapper
                                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                                tooltipKey='Cancel'
                            >
                                <button
                                    className='button secondary'
                                    type='button'
                                    onClick={() => navigate('/taxes')}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                            </TooltipWrapper>
                        </div>
                    </div>
                </form>
            </div>
            <DiscardChangeConfirmModal
                initialData={initialValues}
                inputData={formik.values}
                navBlock={navBlock}
            />

            <Loader isOpen={fetchingAssessmentNotice} />
        </>
    );
}

export default AssessmentNoticeSetup;
